import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-loading-skeleton/dist/skeleton.css';
import { analyticsAggregator } from '../../api';
import { mergeDegraded, resolveRegionTargets } from '../../api/analyticsAggregate';
import { checkAnalyticsAccess, useAnalyticsAccess } from '../../api/analyticsAccess';
import {
  AggregateResult,
  AggregatedMonth,
  AggregatedProducts,
  AggregatedStaffCredits,
  AggregatedSummary,
  AnalyticsReceivableClient,
  AnalyticsTopClient,
  DecimalString,
  RegionDegraded,
  WithRegion,
} from '../../api/types/analytics';
import { getRegionApiUrls, REGION_CODES, RegionCode } from '../../config';
import { useUi } from '../../context/ui.context';
import { usePageTitle } from '../../context/navigationContext';
import DashboardHeader from '../../components/dashboard-header';
import KpiTiles from './components/KpiTiles';
import SectionState from './components/SectionState';
import { ClientsChart, CohortChart, ProductsChart, RevenueChart } from './components/Charts';
import { DebtorsTable, StaffCreditsSection, TopClientsTable } from './components/Tables';
import { formatDateTime, regionName } from './components/format';
import {
  Banner,
  Card,
  ChartGrid,
  Controls,
  HeaderRow,
  Page,
  RefreshButton,
  SegmentButton,
  Segmented,
  SectionTitle,
  Subtle,
} from './components/styles';

type RegionFilter = 'all' | RegionCode;
const PERIODS = [3, 6, 12, 24];
const TOP_LIMIT = 20;
const DEBTORS_LIMIT = 50;
const STAFF_OPS_LIMIT = 100;

interface DashboardData {
  summary: AggregatedSummary | null;
  months: AggregatedMonth[];
  products: AggregatedProducts | null;
  topClients: Array<WithRegion<AnalyticsTopClient>>;
  receivables: { total_usd: DecimalString | null; clients: Array<WithRegion<AnalyticsReceivableClient>> } | null;
  staffCredits: AggregatedStaffCredits | null;
}

const EMPTY: DashboardData = {
  summary: null,
  months: [],
  products: null,
  topClients: [],
  receivables: null,
  staffCredits: null,
};

const DirectorDashboard: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  usePageTitle(t('DD_TITLE'));
  const access = useAnalyticsAccess();
  const [accessOverride, setAccessOverride] = useState<typeof access | null>(null);
  const accessState = (accessOverride ?? access).state;
  const accessRegion = (accessOverride ?? access).region;

  const [regionFilter, setRegionFilter] = useState<RegionFilter>('all');
  const [months, setMonths] = useState<number>(12);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData>(EMPTY);
  const [sections, setSections] = useState<Partial<Record<keyof DashboardData, AggregateResult<unknown>>>>({});
  const [degraded, setDegraded] = useState<RegionDegraded[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const requestId = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const regions = useMemo(
    () => (regionFilter === 'all' ? [...REGION_CODES] : [regionFilter]),
    [regionFilter],
  );
  const aliases = useMemo(() => Object.fromEntries(
    Object.entries(resolveRegionTargets(REGION_CODES, getRegionApiUrls()).aliases)
      .filter(([region]) => regions.includes(region as RegionCode)),
  ), [regions]);

  const load = useCallback(
    async (refresh = false) => {
      const id = ++requestId.current;
      controller.current?.abort();
      controller.current = new AbortController();
      const signal = controller.current.signal;
      setLoading(true);
      setData(EMPTY);
      setSections({});
      setDegraded([]);
      setGeneratedAt(null);
      const safe = <T,>(request: Promise<AggregateResult<T>>): Promise<AggregateResult<T>> =>
        request.catch(() => ({
          data: null, okRegions: [], degradedRegions: [], metas: {},
          failedRegions: regions.map((region) => ({ region, error: 'unavailable' })),
        }));
      const results = await Promise.all([
        safe(analyticsAggregator.summary(regions, { refresh, signal })),
        safe(analyticsAggregator.monthly(regions, { months, refresh, signal })),
        safe(analyticsAggregator.products(regions, { months, refresh, signal })),
        safe(analyticsAggregator.topClients(regions, { months, limit: TOP_LIMIT, refresh, signal })),
        safe(analyticsAggregator.receivables(regions, { limit: DEBTORS_LIMIT, refresh, signal })),
        safe(analyticsAggregator.staffCredits(regions, { months, limit: STAFF_OPS_LIMIT, refresh, signal })),
      ]);
      if (id !== requestId.current || signal.aborted) return;
      const [summary, monthly, products, top, receivables, staff] = results;
      setSections({ summary, months: monthly, products, topClients: top, receivables, staffCredits: staff });
      setDegraded(mergeDegraded(results.map((r) => r.degradedRegions)));
      if (results.every((r) => !r.okRegions.length)) {
        viewAlert({ severity: 'error', message: t('DD_LOAD_ERROR') });
      }
      const stamps = results.flatMap((r) => Object.values(r.metas))
        .map((meta) => meta.generated_at).filter(Boolean).sort();
      setGeneratedAt(stamps[0] || null);

      setData({
        summary: summary.data ?? null,
        months: monthly.data ?? [],
        products: products.data,
        topClients: top.data ?? [],
        receivables: receivables.data,
        staffCredits: staff.data,
      });
      setLoading(false);
    },
    [regions, months, t, viewAlert],
  );

  useEffect(() => {
    if (accessState === 'allowed') void load(false);
    return () => {
      requestId.current += 1;
      controller.current?.abort();
    };
  }, [load, accessState]);

  const retryAccess = async () => {
    setAccessOverride({ ...access, state: 'loading' });
    setAccessOverride(await checkAnalyticsAccess(true));
  };


  const regionOptions: Array<{ value: RegionFilter; label: string }> = [
    { value: 'all', label: t('DD_ALL_REGIONS') },
    ...REGION_CODES.map((r) => ({ value: r as RegionFilter, label: regionName(r) })),
  ];

  if (accessState !== 'allowed') {
    return (
      <Page>
        <DashboardHeader analyticsAvailable={false} />
        {accessState === 'loading' ? (
          <Subtle>…</Subtle>
        ) : (
          <Card role="alert">
            <SectionTitle>
              {accessState === 'denied' ? t('DD_NO_ACCESS_TITLE') : t('DD_LOAD_ERROR')}
            </SectionTitle>
            <Subtle>
              {accessState === 'denied'
                ? t('DD_NO_ACCESS_TEXT', { region: regionName(accessRegion) })
                : t('DD_ACCESS_CHECK_FAILED', { region: regionName(accessRegion) })}
            </Subtle>
            {(accessState === 'error' || accessState === 'denied') && (
              <RefreshButton type="button" style={{ marginTop: 12 }} onClick={retryAccess}>
                {t('DD_RETRY')}
              </RefreshButton>
            )}
          </Card>
        )}
      </Page>
    );
  }

  return (
    <Page>
      <DashboardHeader analyticsAvailable />
      <HeaderRow>
        <Controls>
          <Segmented role="group" aria-label={t('DD_REGION')}>
            {regionOptions.map((o) => (
              <SegmentButton
                key={o.value}
                type="button"
                $active={regionFilter === o.value}
                aria-pressed={regionFilter === o.value}
                onClick={() => setRegionFilter(o.value)}
              >
                {o.label}
              </SegmentButton>
            ))}
          </Segmented>
          <Segmented role="group" aria-label={t('DD_PERIOD')}>
            {PERIODS.map((p) => (
              <SegmentButton
                key={p}
                type="button"
                $active={months === p}
                aria-pressed={months === p}
                onClick={() => setMonths(p)}
              >
                {t('DD_MONTHS_SHORT', { count: p })}
              </SegmentButton>
            ))}
          </Segmented>
          <RefreshButton type="button" disabled={loading} onClick={() => load(true)}>
            {t('DD_REFRESH')}
          </RefreshButton>
        </Controls>
        {generatedAt && <Subtle>{t('DD_GENERATED_AT', { date: formatDateTime(generatedAt) })}</Subtle>}
      </HeaderRow>
      <Subtle style={{ marginTop: -8 }}>{t('DD_PERIOD_NOTE', { count: months })}</Subtle>

      {degraded.length > 0 && (
        <Banner $tone="warning" role="alert">
          {t('DD_REGIONS_DEGRADED', {
            regions: degraded
              .map((d) =>
                d.currencies.length
                  ? `${regionName(d.region)} (${t('DD_UNKNOWN_CURRENCIES', { currencies: d.currencies.join(', ') })})`
                  : regionName(d.region),
              )
              .join(', '),
          })}
        </Banner>
      )}
      <Banner $tone="info">
        {t('DD_FX_NOTE')}
        {Object.entries(aliases).map(([region, owner]) => (
          <div key={region}>
            {t('DD_DEV_SHARED_BACKEND', { region: regionName(region), owner: regionName(owner) })}
          </div>
        ))}
      </Banner>

      <SectionState title={t('DD_KPI_SUMMARY')} result={sections.summary} loading={loading}>
        <KpiTiles summary={data.summary} loading={loading} showBaseCurrency={regionFilter !== 'all'} />
      </SectionState>

      <ChartGrid>
        <SectionState title={t('DD_CHART_REVENUE')} result={sections.months} loading={loading}>
          <RevenueChart months={data.months} loading={loading} />
        </SectionState>
        <SectionState title={t('DD_CHART_CLIENTS')} result={sections.months} loading={loading}>
          <ClientsChart months={data.months} loading={loading} />
        </SectionState>
        <SectionState title={t('DD_CHART_COHORT')} result={sections.months} loading={loading}>
          <CohortChart months={data.months} loading={loading} />
        </SectionState>
        <SectionState title={t('DD_CHART_PRODUCTS')} result={sections.products} loading={loading}>
          <ProductsChart products={data.products} loading={loading} />
        </SectionState>
      </ChartGrid>

      <ChartGrid>
        <SectionState title={t('DD_TOP_CLIENTS')} result={sections.topClients} loading={loading}>
          <TopClientsTable clients={data.topClients} loading={loading} />
        </SectionState>
        <SectionState title={t('DD_DEBTORS')} result={sections.receivables} loading={loading}>
          <DebtorsTable
            clients={data.receivables?.clients ?? []}
            totalUsd={data.receivables?.total_usd ?? null}
            loading={loading}
          />
        </SectionState>
      </ChartGrid>
      <SectionState title={t('DD_STAFF_CREDITS')} result={sections.staffCredits} loading={loading}>
        <StaffCreditsSection data={data.staffCredits} loading={loading} />
      </SectionState>
    </Page>
  );
};

export default DirectorDashboard;
